import React from "react";
import { createRoot, hydrateRoot } from "react-dom/client";
import App from "./app";

// ✅ NEW: deep link handling for Stripe → MyPass on iOS
import { App as CapApp } from "@capacitor/app";
import { Browser } from "@capacitor/browser";

// ✅ gapi safety stub to keep Google iframe helpers from crashing Capacitor
if (typeof window !== "undefined") {
  // Ensure gapi exists so api.js doesn't crash on gapi.iframes.getContext
  window.gapi = window.gapi || {};
  window.gapi.iframes = window.gapi.iframes || {
    getContext: () => null, // return a harmless value
  };
  // Also guard any dynamic updates that might overwrite it
  try {
    Object.freeze(window.gapi.iframes);
  } catch (e) {
    // ignore if freeze fails, it's just a safety net
  }
}

// ✅ NEW: Handle deep links from Stripe (iOS/Capacitor)
CapApp.addListener("appUrlOpen", async (event) => {
  try {
    const url = event?.url || "";
    if (!url) return;

    console.log("[DeepLink] appUrlOpen:", url);

    const u = new URL(url);

    // Supports: pay2pee://mypass?session_id=...
    if (u.hostname === "mypass") {
      const sessionId = u.searchParams.get("session_id");
      if (!sessionId) return;

      try {
        await Browser.close();
      } catch (e) {
        // ignore if browser already closed
      }

      window.location.hash = `#/mypass?session_id=${encodeURIComponent(
        sessionId
      )}`;
      return;
    }

    // Supports: pay2pee://home
    if (u.hostname === "home") {
      try {
        await Browser.close();
      } catch (e) {
        // ignore if browser already closed
      }

      window.location.hash = "#/home";
    }
  } catch (err) {
    console.log("Deep link error:", err);
  }
});

class RootErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    console.error("Root render error:", error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="critical-error">
          <h2>Critical Load Error</h2>
          <p>Something went wrong loading the app UI.</p>
        </div>
      );
    }
    return this.props.children;
  }
}

const container = document.getElementById("root");

const appTree = (
  <React.StrictMode>
    <RootErrorBoundary>
      <App />
    </RootErrorBoundary>
  </React.StrictMode>
);

if (container.hasChildNodes()) {
  hydrateRoot(container, appTree);
} else {
  const root = createRoot(container);
  root.render(appTree);
}